// The live graph, drawn with D3 force on SVG. People and organisations are
// nodes (size: standing label; colour: type; shape: circle for people, square
// for light issuers, diamond for full issuers). Claims are small satellites of
// their holder. Edges run from the attester (or issuer) to the claim, thickness
// by kind. An anchored claim is a diamond; a new anchor pulses, and its anchor
// link pulls the claim and its holder toward the issuer.
import { forceCollide, forceLink, forceManyBody, forceRadial, forceSimulation, forceX, forceY, type Simulation, type SimulationLinkDatum, type SimulationNodeDatum } from "d3-force";
import { select, type Selection } from "d3-selection";
import { zoom, zoomIdentity, zoomTransform, type ZoomTransform } from "d3-zoom";
import { linksOf, type LinkKind } from "./links";
import { activePulses, COLOUR, RADIUS, THICKNESS, type EdgeKind, type GraphState, type LiveEdge, type LiveNode } from "./model";

interface N extends SimulationNodeDatum { id: string; kind: "did" | "claim"; node?: LiveNode; holder?: string; anchored?: boolean;
  comp?: number; isolated?: boolean }
interface L extends SimulationLinkDatum<N> { id: string; kind: LinkKind; via?: "delegated" | "direct" }

const SVGNS = "http://www.w3.org/2000/svg";
/** A tap anywhere within this many screen pixels of a person or organisation selects it. */
const HIT_PX = 22;
/** A pointer that moves further than this between down and up is a pan, not a tap. */
const TAP_SLOP_PX = 10;

/** Above this many edges, faint peer edges are thinned (see applyThinning). */
export const THIN_ABOVE = 500;
const THIN_ZOOM = 1.8;

export type Layout = "moving" | "settled" | "held";

export interface View { nodes: LiveNode[]; edges: LiveEdge[]; claims: [string, string][] }

export function viewOf(g: GraphState): View {
  return { nodes: [...g.nodes.values()], edges: [...g.edges.values()], claims: [...g.claims] };
}

const circlePath = (r: number) => `M${-r},0a${r},${r} 0 1,0 ${2 * r},0a${r},${r} 0 1,0 ${-2 * r},0`;
/** A stable pseudo-random number in [0, 1) for an id: a node starts in the same place on every load. */
function rand(id: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 100000) / 100000;
}
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => ((s = (Math.imul(1664525, s) + 1013904223) >>> 0) / 4294967296);
}

const idOf = (x: string | number | N) => (typeof x === "object" ? x.id : String(x));

/** How far a claim may sit from its holder (graph units). */
function leash(h: N): number {
  return (h.kind === "did" ? visualRadius(h) : 5) + 16;
}

/** Connected components over people and organisations (joined through claims); isolated nodes flagged. */
function markComponents(nodes: Map<string, N>, view: View) {
  const parent = new Map<string, string>();
  const find = (a: string): string => { let r = a; while (parent.get(r) !== r) r = parent.get(r)!; parent.set(a, r); return r; };
  const join = (a: string, b: string) => { if (nodes.has(a) && nodes.has(b)) parent.set(find(a), find(b)); };
  for (const id of nodes.keys()) parent.set(id, id);
  for (const [claimId, holder] of view.claims) join(claimId, holder);
  for (const e of view.edges) {
    join(e.from, e.claim_id); join(e.from, e.to);
    if (e.attester_did) { join(e.attester_did, e.claim_id); join(e.attester_did, e.issuer_did ?? e.from); }
  }
  const ids = new Map<string, number>();
  const size = new Map<string, number>();
  for (const id of nodes.keys()) { const r = find(id); size.set(r, (size.get(r) ?? 0) + 1); }
  for (const [id, n] of nodes) {
    const r = find(id);
    if (!ids.has(r)) ids.set(r, ids.size);
    n.comp = ids.get(r);
    n.isolated = n.kind === "did" && size.get(r) === 1;
  }
}

/** Pull each component's centre of mass gently toward the origin. */
function componentGravity(strength: number) {
  let nodes: N[] = [];
  const force = (alpha: number) => {
    const sums = new Map<number, { x: number; y: number; n: number }>();
    for (const n of nodes) {
      if (n.isolated || n.comp === undefined) continue;
      const s = sums.get(n.comp) ?? { x: 0, y: 0, n: 0 };
      s.x += n.x ?? 0; s.y += n.y ?? 0; s.n++; sums.set(n.comp, s);
    }
    for (const n of nodes) {
      const s = n.comp === undefined ? undefined : sums.get(n.comp);
      if (!s || n.isolated) continue;
      n.vx = (n.vx ?? 0) - (s.x / s.n) * strength * alpha;
      n.vy = (n.vy ?? 0) - (s.y / s.n) * strength * alpha;
    }
  };
  force.initialize = (ns: N[]) => { nodes = ns; };
  return force;
}

function visualRadius(n: N): number {
  if (n.kind === "claim") return 5;
  const r = RADIUS[n.node!.standing];
  return n.node!.type === "issuer" ? r * 1.6 : n.node!.type === "light_issuer" ? r * 1.7 : r;
}

function shape(n: N): string {
  if (n.kind === "claim") return n.anchored ? "M0,-5L5,0L0,5L-5,0Z" : "";
  const r = RADIUS[n.node!.standing];
  if (n.node!.type === "issuer") return `M0,${-r * 1.6}L${r * 1.6},0L0,${r * 1.6}L${-r * 1.6},0Z`;
  if (n.node!.type === "light_issuer") return `M${-r * 1.2},${-r * 1.2}h${r * 2.4}v${r * 2.4}h${-r * 2.4}Z`;
  return "";
}

export class LiveRenderer {
  private sim: Simulation<N, L>;
  private nodes = new Map<string, N>();
  private links: L[] = [];
  private root: Selection<SVGGElement, unknown, null, undefined>;
  private gEdges: Selection<SVGGElement, unknown, null, undefined>;
  private gClaims: Selection<SVGGElement, unknown, null, undefined>;
  private gNodes: Selection<SVGGElement, unknown, null, undefined>;
  private gPulses: Selection<SVGGElement, unknown, null, undefined>;
  private pulsed = new Set<string>();
  private zoomer = zoom<SVGSVGElement, unknown>().scaleExtent([0.05, 6]);
  private fitted = false;
  private userZoomed = false;                  // the viewer zoomed or panned: never re-fit over that
  private size = { w: 1200, h: 800 };
  private k = 1;                               // the current zoom scale (hit areas are sized in screen pixels)
  private ns = 1;
  private fitK = 1;                            // the scale of the last fit: zooming in past THIN_ZOOM× of it shows every edge
  private thinning = false;                    // more than THIN_ABOVE edges in the view
  private focused = false;
  private highlight: { dids: Set<string>; claims: Set<string> } | null = null;
  // One DID's fan: the full graph's positions and view are kept while focused, and restored on leaving.
  private focusDid: string | null = null;
  private saved: { pos: Map<string, { x: number; y: number }>; t: ZoomTransform } | null = null;
  private mode: "normal" | "relayout" | "restore" = "normal";
  private pinned = new Set<string>();
  private skipFit = false;   // an insights row's part of the graph                     // a DID's fan (?did=): never thinned                              // node scale: shrinks shapes so the largest stays within capPx() on screen
  private held = false;                        // a profile is open: the layout holds still
  private press?: { id: string; select: string; x: number; y: number };   // a pointer is down on a node
  private pendingRelayout = false;             // graph changes that arrived while held

  constructor(private svg: SVGSVGElement, private onSelect: (did: string) => void) {
    const s = select(svg);
    this.root = s.append("g");
    // Bottom to top: edges and pulses never take a tap (pointer-events: none in CSS);
    // people and organisations sit above their claims.
    this.gEdges = this.root.append("g").attr("class", "edges");
    this.gClaims = this.root.append("g").attr("class", "claims");
    this.gNodes = this.root.append("g").attr("class", "nodes");
    this.gPulses = this.root.append("g").attr("class", "pulses");
    this.zoomer.on("zoom", (e) => {
      this.root.attr("transform", e.transform); this.k = e.transform.k; this.scaleNodes(); this.applyThinning();
      if (e.sourceEvent) this.userZoomed = true;   // a wheel, pinch or drag, not our own fit
    });
    // A tap selects the node under pointerdown, wherever it is by pointerup: the
    // node is frozen from pointerdown, and the choice never depends on what is
    // under the pointer at pointerup (or on a click event surviving a redraw).
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerCancel);
    s.call(this.zoomer as any).call(this.zoomer.transform as any, zoomIdentity);
    // Forces. A claim is held next to its holder (satellite link, then a hard
    // leash in tick()); the attester's or issuer's link to it only biases where
    // around the holder it sits. Repulsion is short-ranged and every component is
    // pulled gently to the centre, so components stay together and on screen; an
    // isolated node joins a ring near the centre.
    this.sim = forceSimulation<N, L>([])
      .force("charge", forceManyBody<N>().strength((n) => (n.kind === "claim" ? -8 : -90)).distanceMax(240))
      .force("link", forceLink<N, L>([]).id((n) => n.id)
        .distance((l) => (l.kind === "satellite" ? leash((l.source as N)) * 0.75 : l.kind === "anchor" ? 70 : l.kind === "delegation" ? 90 : 80))
        .strength((l) => (l.kind === "satellite" ? 0.9 : l.kind === "anchor" ? 0.25 : l.kind === "delegation" ? 0.08 : 0.12)))
      .force("collide", forceCollide<N>((n) => visualRadius(n) + (n.kind === "claim" ? 2 : 6)))
      // Centring weakens as the graph grows, so a large network keeps its clusters instead of collapsing into one ball.
      .force("x", forceX<N>(0).strength((n) => (n.isolated ? 0 : this.centring())))
      .force("y", forceY<N>(0).strength((n) => (n.isolated ? 0 : this.centring())))
      .force("ring", forceRadial<N>(() => this.ringRadius(), 0, 0).strength((n) => (n.isolated ? 0.2 : 0)))
      .force("components", componentGravity(0.08))
      .randomSource(lcg(0x51d))   // the same graph lays out the same way every time (with rand() below)
      .alphaDecay(0.03)
      .on("tick", () => { this.leashClaims(); this.tick(); })
      .on("end", () => {
        // Fit once the layout settles (first load, after a replay, after new nodes), unless the viewer took over the view.
        if (this.skipFit) this.skipFit = false;   // just restored the full graph's own view
        else if (!this.held && !this.press && (!this.fitted || !this.userZoomed)) { this.fit(!!this.focusDid); this.fitted = true; }
        this.setLayout();
      });
    this.setLayout();
  }

  /** The largest node's on-screen radius, at most: relative to the viewport, capped. */
  private capPx() { return Math.min(22, Math.max(10, Math.min(this.size.w, this.size.h) / 32)); }

  /** Sizes by band, but never oversized: shapes shrink (not the layout) when the zoom would make them too big. */
  private scaleNodes() {
    const maxR = Math.max(10, ...[...this.nodes.values()].map(visualRadius));
    this.ns = Math.min(1, this.capPx() / (maxR * (this.k || 1)));
    this.tickNodes();
    this.sizeHits();
  }

  private tickNodes() {
    const t = (n: N) => `translate(${n.x ?? 0},${n.y ?? 0}) scale(${this.ns})`;
    this.gNodes.selectAll<SVGGElement, N>("g.n").attr("transform", t);
    this.gClaims.selectAll<SVGGElement, N>("g.n").attr("transform", t);
  }

  private centring() {
    const dids = [...this.nodes.values()].filter((n) => n.kind === "did").length;
    return Math.min(0.06, Math.max(0.008, 0.25 / Math.sqrt(Math.max(dids, 1))));
  }

  /** Light up an insights row's nodes, claims and their edges, and dim the rest; null clears. */
  setHighlight(h: { dids: string[]; claim_ids: string[] } | null) {
    this.highlight = h ? { dids: new Set(h.dids), claims: new Set(h.claim_ids) } : null;
    this.applyHighlight();
  }

  private applyHighlight() {
    const h = this.highlight;
    this.svg.setAttribute("data-highlight", h ? "on" : "off");
    const litNode = (n: N) => !h || (n.kind === "claim" ? h.claims.has(n.id) : h.dids.has(n.id));
    this.gNodes.selectAll<SVGGElement, N>("g.n").classed("dim", (n) => !litNode(n));
    this.gClaims.selectAll<SVGGElement, N>("g.n").classed("dim", (n) => !litNode(n));
    this.gEdges.selectAll<SVGLineElement, L>("line").classed("dim", (l) => {
      if (!h) return false;
      const t = idOf(l.target), f = idOf(l.source);
      return l.kind === "delegation" ? !(h.dids.has(f) && h.dids.has(t)) : !h.claims.has(t);
    });
  }

  /** A DID's fan is shown whole: no thinning. */
  setFocused(f: boolean) { this.focused = f; this.applyThinning(); }

  /**
   * Enter or leave one DID's fan (call before update() with the focused view). Entering keeps the
   * full graph's positions and view, and lays the ego-graph out on its own around the DID;
   * leaving puts every node back where it was, with the same view.
   */
  setFocus(did: string | null) {
    if (did === this.focusDid) return;
    if (did && !this.focusDid) {
      const pos = new Map<string, { x: number; y: number }>();
      for (const n of this.nodes.values()) if (n.x !== undefined) pos.set(n.id, { x: n.x, y: n.y! });
      this.saved = pos.size ? { pos, t: zoomTransform(this.svg) } : null;   // nothing drawn yet: nothing to restore
    }
    this.focusDid = did;
    this.mode = did ? "relayout" : this.saved ? "restore" : "normal";
    this.lastKey = "";   // the next update() redraws, whatever the graph's version
    this.setFocused(!!did);
  }

  /**
   * Edge legibility on a big graph: above THIN_ABOVE edges, a grey peer edge is drawn only when
   * its attester or the claim's holder is at the "established" band or higher, until the viewer
   * zooms in past THIN_ZOOM× the fitted scale or focuses a DID. Every other kind always draws.
   */
  private applyThinning() {
    const on = this.thinning && !this.focused && this.k < this.fitK * THIN_ZOOM;
    const strong = (n?: N) => !!n?.node && (n.node.standing === "established" || n.node.standing === "strong");
    this.gEdges.selectAll<SVGLineElement, L>("line").attr("data-thinned", (l) => {
      if (!on || l.kind !== "peer") return null;
      const from = l.source as N, claim = l.target as N;
      return strong(from) || strong(this.nodes.get(claim.holder ?? "")) ? null : "true";
    });
  }

  private ringRadius() {
    const others = [...this.nodes.values()].filter((n) => n.kind === "did" && !n.isolated).length;
    return 30 + 9 * Math.sqrt(others);
  }

  /** Keep every claim within its leash of its holder, however hard another link pulls. */
  private leashClaims() {
    for (const c of this.nodes.values()) {
      if (c.kind !== "claim") continue;
      const h = this.nodes.get(c.holder!);
      if (!h || h.x === undefined || c.x === undefined) continue;
      const dx = c.x - h.x!, dy = c.y! - h.y!, d = Math.hypot(dx, dy), max = leash(h);
      if (d > max) { c.x = h.x! + (dx / d) * max; c.y = h.y! + (dy / d) * max; c.vx = (c.vx ?? 0) * 0.5; c.vy = (c.vy ?? 0) * 0.5; }
    }
  }

  private setLayout() {
    const l: Layout = this.held || this.press ? "held" : this.sim.alpha() > this.sim.alphaMin() ? "moving" : "settled";
    this.svg.setAttribute("data-layout", l);
  }

  /** While a profile is open, hold the layout still; graph changes are drawn in place and laid out on release. */
  setHeld(held: boolean) {
    if (held === this.held) return;
    this.held = held;
    if (held) this.sim.stop();
    else if (!this.press) this.resume();
    this.setLayout();
  }

  private resume() {
    if (this.pendingRelayout || this.sim.alpha() > this.sim.alphaMin()) {
      this.sim.alpha(Math.max(this.sim.alpha(), this.pendingRelayout ? 0.3 : 0)).restart();
      this.pendingRelayout = false;
    }
  }

  /** The person or organisation (or claim) whose centre is nearest a screen point, within a tap's reach. */
  private nearest(cx: number, cy: number): N | undefined {
    const rect = this.svg.getBoundingClientRect(), t = zoomTransform(this.svg);
    const sx = rect.width / this.size.w, sy = rect.height / this.size.h;
    let best: N | undefined, bestD = Infinity;
    for (const n of this.nodes.values()) {
      if (n.x === undefined) continue;
      const px = rect.left + (t.applyX(n.x) + this.size.w / 2) * sx, py = rect.top + (t.applyY(n.y!) + this.size.h / 2) * sy;
      // A claim counts a little further away, so its holder wins a near-tie.
      const d = Math.hypot(px - cx, py - cy) * (n.kind === "claim" ? 1.4 : 1);
      if (d < bestD) { bestD = d; best = n; }
    }
    return bestD <= HIT_PX * 1.5 ? best : undefined;
  }

  private onPointerDown = (ev: PointerEvent, n: N) => {
    if (ev.button !== undefined && ev.button > 0) return;
    const did = n.kind === "claim" ? this.nodes.get(n.holder!) : n;
    this.press = { id: n.id, select: n.kind === "claim" ? n.holder! : n.id, x: ev.clientX, y: ev.clientY };
    this.sim.stop();
    if (did) { did.fx = did.x; did.fy = did.y; }
    this.setLayout();
  };

  private onPointerUp = (ev: PointerEvent) => {
    const p = this.press;
    if (!p) return;
    this.release();
    if (Math.hypot(ev.clientX - p.x, ev.clientY - p.y) <= TAP_SLOP_PX) this.onSelect(p.select);
  };

  private onPointerCancel = () => { if (this.press) this.release(); };

  private release() {
    const p = this.press!;
    this.press = undefined;
    const n = this.nodes.get(p.select);
    if (n) { n.fx = null; n.fy = null; }
    if (!this.held) this.resume();
    this.setLayout();
  }

  /** Hit areas: at least HIT_PX on screen, whatever the zoom. */
  private sizeHits() {
    const k = (this.k || 1) * this.ns;   // screen pixels per unit inside a node
    this.gNodes.selectAll<SVGCircleElement, N>("circle.hit").attr("r", (n) => Math.max(visualRadius(n) * 1.3, HIT_PX / k));
    this.gClaims.selectAll<SVGCircleElement, N>("circle.hit").attr("r", () => Math.max(4, 8 / k));
  }

  private lastKey = "";

  update(view: View, g: GraphState, now = Date.now()) {
    // The same graph (only the clock moved): refresh the pulses, leave the layout alone.
    const key = `${g.version}:${view.nodes.length}:${view.edges.length}:${view.claims.length}`;
    if (key === this.lastKey) { this.drawPulses(g, now); return; }
    this.lastKey = key;
    const keep = new Map<string, N>();
    const anchored = new Set(view.edges.filter((e) => e.kind === "anchor").map((e) => e.claim_id));
    const near = (id?: string) => (id && this.nodes.get(id)) || keep.get(id ?? "");
    const mode = this.mode;
    this.mode = "normal";
    if (mode === "normal" && this.pinned.size) {   // the restored full graph moves again once new data arrives
      for (const id of this.pinned) { const n = this.nodes.get(id); if (n) { n.fx = null; n.fy = null; } }
      this.pinned.clear();
    }
    const saved = mode === "restore" ? this.saved : null;
    if (mode === "restore") this.saved = null;
    for (const n of view.nodes) {
      const prev = this.nodes.get(n.did);
      const obj: N = prev ? Object.assign(prev, { node: n }) : { id: n.did, kind: "did", node: n,
        x: (rand(n.did, 1) - 0.5) * 400, y: (rand(n.did, 2) - 0.5) * 300 };
      if (mode === "relayout") {   // the fan, on its own: the DID in the middle, the rest around it
        const centre = n.did === this.focusDid;
        obj.x = centre ? 0 : (rand(n.did, 1) - 0.5) * 240; obj.y = centre ? 0 : (rand(n.did, 2) - 0.5) * 180;
        obj.vx = 0; obj.vy = 0; obj.fx = null; obj.fy = null;
      }
      const back = saved?.pos.get(n.did);
      if (back) { obj.x = obj.fx = back.x; obj.y = obj.fy = back.y; obj.vx = 0; obj.vy = 0; this.pinned.add(n.did); }
      keep.set(n.did, obj);
    }
    // A new claim starts beside its holder, on the side of its first attester (edges run
    // from the attester to the holder, for a claim of the holder's).
    const attesterOf = new Map<string, string>();
    for (const e of view.edges) if (!attesterOf.has(e.claim_id)) attesterOf.set(e.claim_id, e.from);
    for (const [claimId, holder] of view.claims) {
      if (!keep.has(holder)) continue;
      const prev = this.nodes.get(claimId), h = near(holder)!, a = near(attesterOf.get(claimId));
      const ang = a && a !== h ? Math.atan2((a.y ?? 0) - (h.y ?? 0), (a.x ?? 0) - (h.x ?? 0)) : rand(claimId, 3) * 2 * Math.PI;
      const r = leash(h) * 0.7;
      const obj: N = prev ? Object.assign(prev, { anchored: anchored.has(claimId) }) : { id: claimId, kind: "claim", holder,
        anchored: anchored.has(claimId), x: (h.x ?? 0) + Math.cos(ang) * r, y: (h.y ?? 0) + Math.sin(ang) * r };
      if (mode === "relayout") { obj.x = (h.x ?? 0) + Math.cos(ang) * r; obj.y = (h.y ?? 0) + Math.sin(ang) * r; obj.vx = 0; obj.vy = 0; }
      const back = saved?.pos.get(claimId);
      if (back) { obj.x = obj.fx = back.x; obj.y = obj.fy = back.y; obj.vx = 0; obj.vy = 0; this.pinned.add(claimId); }
      keep.set(claimId, obj);
    }
    markComponents(keep, view);
    this.nodes = keep;
    // A delegated anchor runs from the reviewer who signed it, with a dashed delegation link to the issuer (links.ts).
    this.links = linksOf(view, (id) => keep.has(id)).map((l) => ({ ...l }));

    // Every link is drawn, the holder–claim link too: a claim is always visibly attached to its holder.
    this.gEdges.selectAll<SVGLineElement, L>("line").data(this.links, (l) => l.id)
      .join((enter) => enter.append("line").attr("data-testid", "live-edge"))
      .attr("data-kind", (l) => l.kind).attr("class", (l) => `edge k-${l.kind}`)
      .attr("data-from", (l) => idOf(l.source)).attr("data-to", (l) => idOf(l.target))
      .attr("data-claim", (l) => (l.kind === "delegation" ? null : idOf(l.target))).attr("data-via", (l) => l.via ?? null)
      .attr("stroke-width", (l) => (l.kind === "satellite" || l.kind === "delegation" ? 1.5 : THICKNESS[l.kind as EdgeKind]));

    const nodes = [...keep.values()];
    const join = (layer: typeof this.gNodes, data: N[]) => layer.selectAll<SVGGElement, N>("g.n").data(data, (n) => n.id)
      .join((enter) => {
        const gg = enter.append("g").attr("class", "n").attr("tabindex", "0").attr("role", "button");
        gg.append("circle").attr("class", "hit");                     // transparent, generous, on top of its own shape
        gg.insert(() => document.createElementNS(SVGNS, "path"), "circle.hit").attr("class", "shape");
        // Where hit areas overlap (a dense graph), the tap goes to the node whose centre is nearest.
        gg.on("pointerdown", (ev: PointerEvent, n) => this.onPointerDown(ev, this.nearest(ev.clientX, ev.clientY) ?? n))
          .on("keydown", (ev: KeyboardEvent, n) => { if (ev.key === "Enter") this.onSelect(n.kind === "claim" ? n.holder! : n.id); });
        return gg;
      })
      .attr("data-testid", (n) => (n.kind === "did" ? "live-node" : "live-claim"))
      .attr("data-did", (n) => (n.kind === "did" ? n.id : null))
      .attr("data-type", (n) => n.node?.type ?? null)
      .attr("data-standing", (n) => n.node?.standing ?? null)
      .attr("data-anchored", (n) => (n.kind === "claim" ? String(!!n.anchored) : null))
      .attr("data-claim", (n) => (n.kind === "claim" ? n.id : null))
      .attr("data-holder", (n) => (n.kind === "claim" ? n.holder! : null))
      .attr("aria-label", (n) => (n.kind === "did" ? `${n.node!.type.replace("_", " ")}` : "claim"))
      .each(function (n) {
        // Update the shape in place: a redraw never replaces the element under a pointer.
        const el = (this as SVGGElement).querySelector("path.shape") as SVGPathElement;
        el.setAttribute("d", shape(n) || circlePath(n.kind === "claim" ? 2.6 : RADIUS[n.node!.standing]));
        // Claims in claim styling (the legend's grey diamond when anchored), never in an issuer's colour:
        // the anchor shows as its edge, not as another issuer.
        el.setAttribute("fill", n.kind === "claim" ? (n.anchored ? "var(--muted)" : "var(--faint)") : COLOUR[n.node!.type]);
        if (n.node?.standing === "compromised") { el.setAttribute("stroke", "var(--danger)"); el.setAttribute("stroke-width", "2"); }
        else { el.removeAttribute("stroke"); el.removeAttribute("stroke-width"); }
      });
    // Larger nodes on top, so a small neighbour's generous hit area never covers a big node's body.
    join(this.gNodes, nodes.filter((n) => n.kind === "did").sort((a, b) => visualRadius(a) - visualRadius(b))).order();
    join(this.gClaims, nodes.filter((n) => n.kind === "claim")).order();
    this.scaleNodes();

    const fresh = this.drawPulses(g, now);

    this.thinning = view.edges.length > THIN_ABOVE;
    this.applyThinning();
    this.applyHighlight();
    this.sim.nodes(nodes);
    (this.sim.force("link") as any).links(this.links);
    if (mode === "relayout") {
      this.fitted = false;
      this.sim.alpha(1).restart();
    } else if (mode === "restore") {
      // The full graph exactly as it was: its positions (pinned) and its view; only new nodes move.
      if (saved) select(this.svg).call(this.zoomer.transform as any, saved.t);
      this.skipFit = true;
      this.sim.alpha(0.3).restart();
    } else if (this.held || this.press) {
      // Hold still: new nodes appear where they were placed (next to their holder); lay out on release.
      this.pendingRelayout = true;
    } else {
      this.sim.alpha(fresh ? 0.9 : Math.max(this.sim.alpha(), 0.3)).restart();
    }
    this.setLayout();
    this.tick();
  }

  /** A ring on the anchored claim and on the issuer while each pulse lasts; true if one is new. */
  private drawPulses(g: GraphState, now: number): boolean {
    const pulses = activePulses(g, now).filter((p) => this.nodes.has(p.claimId));
    this.gPulses.selectAll<SVGGElement, (typeof pulses)[number]>("g.pulse").data(pulses, (p) => p.edgeId)
      .join((enter) => {
        const gg = enter.append("g").attr("class", "pulse").attr("data-testid", "anchor-pulse");
        gg.append("circle").attr("class", "ring ring-claim").attr("r", 6);
        gg.append("circle").attr("class", "ring ring-issuer").attr("r", 10);
        return gg;
      })
      .attr("data-claim", (p) => p.claimId).attr("data-issuer", (p) => p.issuer);
    const fresh = pulses.some((p) => !this.pulsed.has(p.edgeId));
    for (const p of pulses) this.pulsed.add(p.edgeId);
    this.tick();
    return fresh;
  }

  private tick() {
    this.gEdges.selectAll<SVGLineElement, L>("line")
      .attr("x1", (l) => (l.source as N).x ?? 0).attr("y1", (l) => (l.source as N).y ?? 0)
      .attr("x2", (l) => (l.target as N).x ?? 0).attr("y2", (l) => (l.target as N).y ?? 0);
    this.tickNodes();
    this.gPulses.selectAll<SVGGElement, { claimId: string; issuer: string }>("g.pulse").each((p, i, els) => {
      const c = this.nodes.get(p.claimId), iss = this.nodes.get(p.issuer);
      const g = select(els[i]);
      g.select(".ring-claim").attr("cx", c?.x ?? 0).attr("cy", c?.y ?? 0);
      g.select(".ring-issuer").attr("cx", iss?.x ?? 0).attr("cy", iss?.y ?? 0);
    });
  }

  /**
   * Fit the whole graph into the viewport, inside padding that clears the
   * overlays (title and counts on top, filter and legend at the bottom). The
   * scale is capped so that on a small graph the largest node stays a sensible
   * size for the screen.
   */
  fit(animate = false) {
    const ns = [...this.nodes.values()];
    if (!ns.length) return;
    const { w, h } = this.size;
    const pad = { x: Math.max(16, w * 0.04), top: h < 600 ? 70 : 110, bottom: h < 600 ? 60 : 80 };
    const x0 = Math.min(...ns.map((n) => (n.x ?? 0) - visualRadius(n))), x1 = Math.max(...ns.map((n) => (n.x ?? 0) + visualRadius(n)));
    const y0 = Math.min(...ns.map((n) => (n.y ?? 0) - visualRadius(n))), y1 = Math.max(...ns.map((n) => (n.y ?? 0) + visualRadius(n)));
    // Fill the padded viewport (up to 2.2x); scaleNodes() keeps the shapes from growing past capPx().
    const k = Math.max(0.05, Math.min(2.2, (w - 2 * pad.x) / Math.max(x1 - x0, 1), (h - pad.top - pad.bottom) / Math.max(y1 - y0, 1)));
    this.fitK = k;
    const t = zoomIdentity.translate(0, (pad.top - pad.bottom) / 2).scale(k).translate(-(x0 + x1) / 2, -(y0 + y1) / 2);
    if (!animate) { select(this.svg).call(this.zoomer.transform as any, t); return; }
    // A short transition into the fan.
    const from = zoomTransform(this.svg), start = performance.now(), ms = 400;
    const step = (now: number) => {
      const u = Math.min(1, (now - start) / ms), e = u * (2 - u);
      const at = zoomIdentity.translate(from.x + (t.x - from.x) * e, from.y + (t.y - from.y) * e).scale(from.k + (t.k - from.k) * e);
      select(this.svg).call(this.zoomer.transform as any, at);
      if (u < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  resize(width: number, height: number) {
    const changed = width !== this.size.w || height !== this.size.h;
    this.size = { w: width, h: height };
    select(this.svg).attr("viewBox", `${-width / 2} ${-height / 2} ${width} ${height}`);
    // Re-fit to the new window, unless the viewer has zoomed or panned.
    if (changed && this.fitted && !this.userZoomed && !this.held && !this.press) this.fit();
  }

  stop() {
    this.sim.stop();
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerCancel);
  }
}
